import { createConsumer } from '@rails/actioncable';

let stopChat = null;

function initChat() {
  if (stopChat) stopChat();
  const root = document.getElementById('system-chat');
  if (!root) return;
  root.classList.add('skip-loading');
  root.querySelectorAll('button, form').forEach(element => element.classList.add('skip-loading'));
  const me = Number(root.dataset.userId);
  const sidebar = root.querySelector('.chat-sidebar');
  const usersList = root.querySelector('.chat-users');
  const search = root.querySelector('input[type="search"]');
  const statusLabel = root.querySelector('.chat-connection[role="status"]');
  const windowsRoot = root.querySelector('.chat-windows');
  // Turbolinks puede restaurar el HTML anterior, pero no sus manejadores.
  windowsRoot.replaceChildren();
  const windows = new Map();
  const seenEvents = new Set();
  const abort = new AbortController();
  const originalTitle = document.title;
  const favicon = document.querySelector('link[rel="shortcut icon"]');
  const originalIcon = favicon && favicon.href;
  let stopped = false, consumer = null, unread = {}, total = null, userPage = 1;
  let lastActivity = Date.now(), searchTimer, toastTimer, polling = false, searchVersion = 0;
  let cableConnected = false, lastFullPoll = 0, lastUsersLoad = 0, usersLoaded = false, notificationTimer;
  let lastHistoryReconcile = Date.now();
  let audio = null, muted = false, soundReady = false;
  function storage(key, value) {
    try {
      if (value === undefined) return sessionStorage.getItem(`uni-chat:${me}:${key}`);
      sessionStorage.setItem(`uni-chat:${me}:${key}`, value);
    } catch (_) { /* El chat sigue disponible sin almacenamiento local. */ }
    return null;
  }
  muted = storage('muted') === 'true';
  const route = (name, id) => {
    if (id < 0 && ['messagesUrl', 'readUrl'].includes(name)) name = name === 'messagesUrl' ? 'groupMessagesUrl' : 'groupReadUrl';
    return root.dataset[name].replace(/:user_id|%3Auser_id|:id|%3Aid/g, String(Math.abs(id)));
  };
  let groupPage = 1, groupUnread = {}, groupsLoadedAt = 0, membersVersion = 0;
  const openGroup = room => openChat({ id: -room.id, name: room.name });
  async function loadGroups(reset = true) {
    if (!root.dataset.conversationsUrl) return;
    if (reset) groupPage = 1;
    try {
      const data = await request(`${root.dataset.conversationsUrl}?page=${groupPage}`);
      if (stopped) return;
      const list = root.querySelector('.chat-groups');
      if (reset) list.replaceChildren();
      data.conversations.forEach(room => {
        const button = text('button', '', 'chat-group-entry'); button.type = 'button'; button.dataset.groupId = room.id;
        button.append(text('span', room.support ? 'Administración' : 'Grupo', 'chat-faculty'), text('strong', room.name));
        const badge = text('span', room.unread || 0, 'chat-user-count'); badge.hidden = !room.unread;
        button.append(badge); button.onclick = () => openGroup(room); list.append(button);
      });
      root.querySelector('.chat-more-groups').hidden = !data.more;
      groupsLoadedAt = Date.now();
    } catch (error) { statusLabel.textContent = error.message; }
  }
  async function manageMembers(state = null) {
    const dialog = root.querySelector('.chat-members-dialog');
    const generation = ++membersVersion;
    const active = () => !stopped && dialog.open && membersVersion === generation;
    dialog.replaceChildren();
    const close = text('button', 'Cerrar', 'chat-dialog-close'); close.type = 'button'; close.onclick = () => dialog.close();
    const heading = text('h3', state?.id < 0 ? 'Participantes' : 'Crear conversación grupal');
    const note = text('p', state?.id > 0 ? 'Se creará un grupo nuevo. Los mensajes privados anteriores no se compartirán.' : 'Los nuevos participantes solo verán los mensajes enviados después de incorporarse.');
    const error = text('p', '', 'chat-error');
    dialog.append(close, heading, note, error); dialog.showModal();
    let fixed = [me], support = false;
    try {
      if (state?.id < 0) {
        const info = await request(route('groupInfoUrl', state.id));
        if (!active()) return;
        fixed = info.participants.map(user => user.id); support = info.support;
        dialog.append(text('p', info.participants.map(user => user.name).join(' · '), 'chat-participants'));
      } else if (state) fixed.push(state.id);
      if (support) { note.textContent = 'Conversación con todos los administradores activos del sistema.'; return; }
      const title = document.createElement('input'); title.maxLength = 80; title.placeholder = 'Nombre del grupo (opcional)'; title.setAttribute('aria-label', 'Nombre del grupo');
      if (!state || state.id > 0) dialog.append(title);
      const input = document.createElement('input'); input.type = 'search'; input.placeholder = 'Buscar personas (mínimo 2 letras)'; input.setAttribute('aria-label', 'Buscar participantes');
      const selected = new Map(), results = text('div', '', 'chat-member-results'), selection = text('p', '', 'chat-participants');
      const submit = text('button', state?.id < 0 ? 'Agregar personas' : 'Crear grupo', 'chat-send'); submit.type = 'button';
      dialog.append(input, results, selection, submit);
      let version = 0, timer;
      async function find() {
        const current = ++version;
        if (input.value.trim().length === 1) return;
        try {
          const data = await request(`${root.dataset.usersUrl}?q=${encodeURIComponent(input.value)}`);
          if (current !== version || !active()) return;
          results.replaceChildren();
          data.users.filter(user => !fixed.includes(user.id)).forEach(user => {
            const label = text('label', '', 'chat-member-option'), check = document.createElement('input'); check.type = 'checkbox'; check.checked = selected.has(user.id);
            check.onchange = () => { if (check.checked) selected.set(user.id, user); else selected.delete(user.id); selection.textContent = Array.from(selected.values()).map(person => person.name).join(' · '); };
            label.append(check, text('span', `${user.name}${user.faculty ? ` · ${user.faculty}` : ''}`)); results.append(label);
          });
          if (!results.children.length) results.append(text('p', 'No se encontraron otras personas.'));
        } catch (failure) { error.textContent = failure.message; }
      }
      input.oninput = () => { clearTimeout(timer); timer = setTimeout(find, 300); };
      submit.onclick = async () => {
        if (!selected.size) { error.textContent = 'Selecciona al menos una persona.'; return; }
        submit.disabled = true;
        try {
          const room = await request(state?.id < 0 ? route('groupMembersUrl', state.id) : root.dataset.conversationsUrl, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_ids: state?.id < 0 ? [...selected.keys()] : [...fixed.filter(id => id !== me), ...selected.keys()], title: title.value })
          });
          if (active()) dialog.close();
          if (!stopped) { loadGroups(); openGroup(room); }
        } catch (failure) { error.textContent = failure.message; }
        finally { submit.disabled = false; }
      };
      await find();
    } catch (failure) { error.textContent = failure.message; }
  }
  async function request(url, options = {}) {
    const headers = { Accept: 'application/json', ...options.headers };
    if (options.method && options.method !== 'GET') headers['X-CSRF-Token'] = document.querySelector('meta[name="csrf-token"]').content;
    const response = await fetch(url, { credentials: 'same-origin', signal: abort.signal, ...options, headers });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        consumer?.disconnect();
        throw new Error('La sesión terminó. Vuelva a iniciar sesión para usar el chat.');
      }
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || (response.status === 410 ? 'La imagen ya caducó.' : 'No se pudo completar la operación.'));
    }
    return response.status === 204 ? null : response.json();
  }
  const text = (tag, content, className) => {
    const element = document.createElement(tag);
    element.textContent = content || '';
    if (className) element.className = className;
    if (tag === 'button' || tag === 'form') element.classList.add('skip-loading');
    return element;
  };
  function sound() {
    if (muted || !audio || !soundReady) return;
    try {
      const oscillator = audio.createOscillator(), gain = audio.createGain();
      oscillator.connect(gain); gain.connect(audio.destination);
      oscillator.frequency.setValueAtTime(660, audio.currentTime);
      oscillator.frequency.setValueAtTime(880, audio.currentTime + .11);
      gain.gain.setValueAtTime(.035, audio.currentTime);
      gain.gain.exponentialRampToValueAtTime(.001, audio.currentTime + .28);
      oscillator.start(); oscillator.stop(audio.currentTime + .3);
    } catch (_) { /* Navegadores que bloquean audio: permanecen los avisos visuales. */ }
  }
  function activity() {
    lastActivity = Date.now();
    if (!soundReady && !muted) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        audio ||= new AudioContext();
        audio.resume().then(() => { soundReady = true; }).catch(() => {});
      }
    }
  }
  document.addEventListener('pointerdown', activity);
  document.addEventListener('keydown', activity);
  function tabBadge() {
    document.title = total ? `(${total}) ✉ ${originalTitle}` : originalTitle;
    if (!favicon) return;
    if (!total) { favicon.href = originalIcon; return; }
    const img = new Image();
    img.onload = () => {
      if (stopped || !total) return;
      try {
        const canvas = document.createElement('canvas'); canvas.width = 32; canvas.height = 32;
        const ctx = canvas.getContext('2d'); ctx.drawImage(img, 0, 0, 32, 32);
        ctx.fillStyle = '#c74451'; ctx.beginPath(); ctx.arc(24, 8, 8, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = 'white'; ctx.font = 'bold 10px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(total > 9 ? '+' : String(total), 24, 12);
        favicon.href = canvas.toDataURL('image/png');
      } catch (_) { /* El título conserva el indicador si el favicon no se puede dibujar. */ }
    };
    img.src = originalIcon;
  }
  function updateCounts(data) {
    unread = data.unread; total = data.total;
    groupUnread = data.group_unread || {};
    root.querySelectorAll('[data-group-id]').forEach(button => {
      const count = button.querySelector('.chat-user-count'); count.textContent = groupUnread[button.dataset.groupId] || 0; count.hidden = !groupUnread[button.dataset.groupId];
    });
    const badge = root.querySelector('.chat-unread-total'); badge.hidden = !total; badge.textContent = total > 99 ? '99+' : total;
    root.querySelector('.chat-launcher').setAttribute('aria-label', `Abrir chat, ${total} mensajes sin leer`);
    tabBadge();
    usersList.querySelectorAll('.chat-user').forEach(button => {
      const count = button.querySelector('.chat-user-count');
      count.textContent = unread[button.dataset.userId] || 0; count.hidden = !unread[button.dataset.userId];
    });
  }
  function toast(peerId) {
    const toast = root.querySelector('.chat-toast');
    toast.textContent = 'Tienes un nuevo mensaje. Haz clic para abrirlo.';
    toast.hidden = false; toast.onclick = () => { openChat({ id: peerId, name: 'Conversación' }); toast.hidden = true; };
    clearTimeout(toastTimer); toastTimer = setTimeout(() => { toast.hidden = true; }, 6000);
  }
  async function poll(notify = false, heartbeatOnly = false) {
    if (polling || stopped) return;
    polling = true;
    try {
      const data = await request(`${root.dataset.statusUrl}?active=${!document.hidden && Date.now() - lastActivity < 180000}&heartbeat_only=${heartbeatOnly}`);
      if (data.heartbeat) return;
      lastFullPoll = Date.now();
      if (notify && total !== null && data.total > total) sound();
      updateCounts(data);
      if (!consumer) connect();
      statusLabel.textContent = 'Chat privado y grupal · Presencia aproximada';
    } catch (error) { if (!stopped) statusLabel.textContent = error.message; }
    finally { polling = false; }
  }
  function connect() {
    consumer = createConsumer();
    consumer.subscriptions.create('ChatChannel', {
      connected() { cableConnected = true; document.dispatchEvent(new Event('uni:notifications:connected')); statusLabel.textContent = 'Chat conectado'; windows.forEach(state => { if (!state.minimized && !document.hidden) sync(state); }); poll(); },
      disconnected() { cableConnected = false; if (!stopped) statusLabel.textContent = 'Reconectando…'; },
      rejected() { cableConnected = false; statusLabel.textContent = 'Conexión no autorizada. Actualice la página.'; },
      received(event) {
        if (stopped) return;
        if (event.type === 'system_notification') {
          document.dispatchEvent(new CustomEvent('uni:notifications:changed', { detail: event }));
          return;
        }
        if (event.conversation_id) {
          const id = -Number(event.conversation_id), key = `g:${event.conversation_id}:${event.id}`;
          if (event.type === 'group_message' && !seenEvents.has(key)) {
            seenEvents.add(key); if (seenEvents.size > 200) seenEvents.delete(seenEvents.values().next().value);
            if (Number(event.sender_id) !== me) { sound(); if (!windows.has(id) || document.hidden || windows.get(id).minimized) toast(id); }
          }
          const state = windows.get(id);
          if (state && !state.minimized && !document.hidden && event.type !== 'group_read') sync(state);
          groupsLoadedAt = 0;
          clearTimeout(notificationTimer);
          notificationTimer = setTimeout(() => { poll(); if (!sidebar.hidden) loadGroups(); }, 400);
          return;
        }
        const peerId = Number(event.sender_id) === me ? Number(event.recipient_id) : Number(event.sender_id);
        if (event.type === 'read') {
          const state = windows.get(peerId);
          if (state) {
            state.messages.forEach(message => {
              if (message.sender_id === me && message.id <= event.through_id) message.read = true;
            });
            renderMessages(state, 'new');
          }
          return;
        }
        if (event.type === 'message' && !seenEvents.has(event.id)) {
          seenEvents.add(event.id); if (seenEvents.size > 200) seenEvents.delete(seenEvents.values().next().value);
          if (Number(event.recipient_id) === me) { sound(); if (!windows.has(peerId) || document.hidden || windows.get(peerId).minimized) toast(peerId); }
        }
        const state = windows.get(peerId);
        if (state && !state.minimized && !document.hidden) sync(state);
        usersLoaded = false;
        clearTimeout(notificationTimer);
        notificationTimer = setTimeout(() => poll(), 400);
      }
    });
  }
  async function loadUsers(reset = false) {
    const version = ++searchVersion;
    if (reset) userPage = 1;
    try {
      if (search.value.trim().length === 1) {
        usersList.replaceChildren(text('p', 'Escribe al menos 2 caracteres para buscar.', 'chat-empty'));
        root.querySelector('.chat-more-users').hidden = true;
        return;
      }
      const data = await request(`${root.dataset.usersUrl}?q=${encodeURIComponent(search.value)}&page=${userPage}`);
      if (version !== searchVersion || stopped) return;
      if (reset) usersList.replaceChildren();
      data.users.forEach(user => {
        const button = text('button', '', 'chat-user skip-loading'); button.type = 'button'; button.dataset.userId = user.id;
        const avatar = text('span', user.name.split(' ').filter(Boolean).slice(0, 2).map(part => part[0]).join(''), 'chat-avatar');
        const presence = ['online', 'away'].includes(user.state) ? user.state : 'offline';
        const label = { online: 'En línea', away: 'Ausente', offline: 'Desconectado' }[presence];
        const dot = text('span', '', `chat-presence ${presence}`);
        dot.title = label; avatar.append(dot);
        const name = text('span', '', 'chat-user-name'); name.append(text('strong', user.name), text('small', user.dependency));
        if (user.faculty) {
          const faculty = text('span', user.faculty, `chat-faculty${user.same_faculty ? ' same-faculty' : ''}`);
          faculty.title = user.same_faculty ? `${user.faculty} · Tu facultad` : user.faculty;
          name.append(faculty);
        }
        name.append(text('small', label, `chat-state-label ${presence}`));
        const badge = text('span', unread[user.id] || 0, 'chat-user-count'); badge.hidden = !unread[user.id];
        button.append(avatar, name, badge); button.addEventListener('click', () => openChat(user)); usersList.append(button);
      });
      if (!usersList.children.length) usersList.append(text('p', 'No se encontraron usuarios.', 'chat-empty'));
      root.querySelector('.chat-more-users').hidden = !data.more;
      usersLoaded = true; lastUsersLoad = Date.now();
    } catch (error) { statusLabel.textContent = error.message; }
  }
  function rememberWindows() {
    storage('windows', JSON.stringify(Array.from(windows.values()).map(state => ({ id: state.id, name: state.name, minimized: state.minimized }))));
  }
  function renderMessages(state, mode = 'latest') {
    const log = state.element.querySelector('.chat-log');
    const oldHeight = log.scrollHeight, oldTop = log.scrollTop;
    const nearBottom = oldHeight - oldTop - log.clientHeight < 60;
    log.replaceChildren();
    const messages = Array.from(state.messages.values()).sort((a, b) => a.id - b.id);
    if (!messages.length) log.append(text('p', state.id < 0 ? 'Comienza la conversación con los participantes del grupo.' : 'Comienza la conversación. Solo tú y este usuario pueden verla.', 'chat-empty'));
    messages.forEach(message => {
      const row = text('div', '', `chat-message${message.sender_id === me ? ' own' : ''}`);
      const bubble = text('div', message.body, 'chat-bubble');
      if (state.id < 0) bubble.prepend(text('strong', message.sender_name, 'chat-sender-name'));
      if (message.image) {
        const button = text('button', '🖼 Abrir imagen temporal', 'chat-image-open'); button.type = 'button';
        button.addEventListener('click', async () => {
          try {
            const data = await request(route(state.id < 0 ? 'groupImageUrl' : 'viewImageUrl', message.id), { method: 'POST' });
            const dialog = root.querySelector('.chat-image-dialog'); dialog.querySelector('img').src = data.url;
            dialog.showModal();
          } catch (error) { state.error.textContent = error.message; }
        });
        bubble.append(button);
      } else if (message.image_expired) bubble.append(text('small', 'Imagen caducada'));
      const time = new Date(message.created_at);
      const stamp = text('time', `${time.toLocaleString('es', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}${message.sender_id === me && state.id > 0 ? (message.read ? ' · Leído' : ' · Enviado') : ''}`);
      stamp.dateTime = message.created_at; row.append(bubble, stamp); log.append(row);
    });
    if (mode === 'older') log.scrollTop = Math.max(0, log.scrollHeight - oldHeight + oldTop);
    else if (mode === 'latest' || nearBottom) log.scrollTop = log.scrollHeight;
  }
  async function markRead(state) {
    if (document.hidden || state.minimized || state.historyMode || !state.latest || state.closed) return;
    const log = state.element.querySelector('.chat-log');
    if (log.scrollHeight - log.scrollTop - log.clientHeight > 60 || state.reading || state.readThrough === state.latest) return;
    state.reading = true;
    const through = state.latest;
    try {
    await request(route('readUrl', state.id), { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ through_id: state.latest }) });
    state.readThrough = through;
    await poll();
    } finally { state.reading = false; }
  }
  async function history(state, older = false) {
    if (state.loading || state.closed) return;
    state.loading = true;
    const historyButton = state.element.querySelector('.chat-history'); historyButton.disabled = true;
    try {
      const ids = Array.from(state.messages.keys());
      const suffix = older && ids.length ? `?before_id=${Math.min(...ids)}` : '';
      const data = await request(route('messagesUrl', state.id) + suffix);
      if (state.closed || stopped) return;
      state.name = data.user.name; state.element.querySelector('.chat-peer-name').textContent = state.name;
      if (!older) state.messages.clear();
      data.messages.forEach(message => state.messages.set(message.id, message));
      // Ventana acotada incluso si se carga repetidamente el historial.
      const sorted = Array.from(state.messages.keys()).sort((a, b) => a - b);
      while (sorted.length > 300) state.messages.delete(older ? sorted.pop() : sorted.shift());
      state.historyMode = older;
      state.latest = Math.max(older ? state.latest : 0, ...data.messages.map(message => message.id));
      historyButton.hidden = !data.more;
      renderMessages(state, older ? 'older' : 'latest'); rememberWindows();
      if (!older) await markRead(state);
    } catch (error) { if (!stopped) state.error.textContent = error.message; }
    finally {
      state.loading = false; historyButton.disabled = false;
      if (state.syncAgain) { state.syncAgain = false; sync(state); }
    }
  }
  async function sync(state) {
    if (state.closed || stopped) return;
    if (state.loading) { state.syncAgain = true; return; }
    if (!state.latest) { await history(state); return; }
    if (state.syncing) { state.syncAgain = true; return; }
    state.syncing = true;
    try {
      let more;
      do {
        const data = await request(`${route('messagesUrl', state.id)}?after_id=${state.latest}`);
        if (state.closed || stopped) return;
        data.messages.forEach(message => state.messages.set(message.id, message));
        state.latest = Math.max(state.latest, ...data.messages.map(message => message.id));
        more = data.more;
      } while (more);
      const sorted = Array.from(state.messages.keys()).sort((a, b) => a - b);
      while (sorted.length > 300) { state.messages.delete(sorted.shift()); state.element.querySelector('.chat-history').hidden = false; }
      renderMessages(state, 'new'); await markRead(state);
    } catch (error) { if (!stopped) state.error.textContent = error.message; }
    finally { state.syncing = false; if (state.syncAgain) { state.syncAgain = false; sync(state); } }
  }
  function openChat(user) {
    const id = Number(user.id);
    if (windows.has(id)) {
      const state = windows.get(id); state.minimized = false; state.element.classList.remove('minimized');
      state.element.querySelector('textarea').focus(); rememberWindows(); sync(state); return;
    }
    const max = window.innerWidth < 768 ? 1 : 2;
    while (windows.size >= max) {
      const oldest = windows.values().next().value;
      if (oldest.sending) return;
      closeChat(oldest);
    }
    const element = text('section', '', 'chat-window'); element.setAttribute('aria-label', `Conversación con ${user.name}`);
    // HTML estático: todos los datos de usuarios y mensajes se insertan con textContent.
    element.innerHTML = `<header class="chat-panel-header"><strong class="chat-peer-name"></strong><div class="chat-header-actions"><button type="button" class="chat-minimize" aria-label="Minimizar conversación"><i class="bi bi-dash-lg"></i></button><button type="button" class="chat-close" aria-label="Cerrar conversación"><i class="bi bi-x-lg"></i></button></div></header><div class="chat-window-body"><div class="d-flex"><button type="button" class="chat-history flex-grow-1">Mostrar mensajes anteriores</button><button type="button" class="chat-latest chat-history">Últimos</button></div><div class="chat-log" aria-label="Mensajes"></div><form class="chat-composer"><textarea rows="2" maxlength="2000" placeholder="Escribe un mensaje…" aria-label="Mensaje"></textarea><div class="chat-error" role="alert"></div><div class="chat-file-name"></div><div class="chat-compose-actions"><input type="file" accept="image/jpeg,image/png,image/webp" hidden><button type="button" class="chat-attach" aria-label="Adjuntar o quitar imagen" title="JPEG, PNG o WebP · máximo 5 MB"><i class="bi bi-image"></i></button><button type="submit" class="chat-send">Enviar <i class="bi bi-send"></i></button></div></form></div>`;
    element.querySelectorAll('button, form').forEach(control => control.classList.add('skip-loading'));
    const state = { id, name: user.name, element, messages: new Map(), latest: 0, minimized: false, error: element.querySelector('.chat-error') };
    const membersButton = text('button', '', 'chat-members'); membersButton.type = 'button'; membersButton.title = id < 0 ? 'Participantes / agregar personas' : 'Agregar personas en un grupo nuevo'; membersButton.setAttribute('aria-label', membersButton.title); membersButton.innerHTML = '<i class="bi bi-person-plus"></i>';
    membersButton.onclick = () => manageMembers(state); element.querySelector('.chat-header-actions').prepend(membersButton);
    element.querySelector('.chat-peer-name').textContent = user.name;
    const composer = element.querySelector('form'), textarea = element.querySelector('textarea'), file = element.querySelector('input[type="file"]');
    element.querySelector('.chat-log').addEventListener('scroll', () => markRead(state).catch(() => {}));
    textarea.value = storage(`draft:${id}`) || '';
    textarea.addEventListener('input', () => storage(`draft:${id}`, textarea.value));
    textarea.addEventListener('keydown', event => {
      if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) { event.preventDefault(); composer.requestSubmit(); }
    });
    element.querySelector('.chat-attach').onclick = () => {
      if (file.files.length) { file.value = ''; element.querySelector('.chat-file-name').textContent = ''; } else file.click();
    };
    file.onchange = () => {
      const selected = file.files[0]; state.error.textContent = '';
      if (selected && (selected.size > 5 * 1024 * 1024 || !['image/jpeg', 'image/png', 'image/webp'].includes(selected.type))) {
        state.error.textContent = 'Seleccione una imagen JPEG, PNG o WebP de hasta 5 MB.'; file.value = '';
      }
      element.querySelector('.chat-file-name').textContent = file.files[0] ? `${file.files[0].name} · pulsa el icono para quitarla` : '';
    };
    composer.onsubmit = async event => {
      event.preventDefault(); if (state.sending || (!textarea.value.trim() && !file.files.length)) return;
      state.sending = true; state.error.textContent = ''; const button = element.querySelector('.chat-send'); button.disabled = true;
      const submittedText = textarea.value, submittedFile = file.files[0];
      const signature = submittedText + (submittedFile?.name || '') + (submittedFile?.lastModified || '');
      if (state.signature !== signature) { state.signature = signature; state.clientId = crypto.randomUUID(); }
      const data = new FormData(); data.append('body', submittedText); data.append('client_id', state.clientId);
      if (submittedFile) data.append('image', submittedFile);
      try {
        const message = await request(route('messagesUrl', id), { method: 'POST', body: data });
        // El cursor solo avanza al consultar: puede haber mensajes entrantes
        // anteriores a este envío que todavía no llegaron por WebSocket.
        state.messages.set(message.id, message);
        if (textarea.value === submittedText) textarea.value = '';
        if (file.files[0] === submittedFile) {
          file.value = ''; element.querySelector('.chat-file-name').textContent = '';
        }
        state.signature = null; storage(`draft:${id}`, textarea.value);
        renderMessages(state); await sync(state);
      } catch (error) { if (!stopped) state.error.textContent = error.message; }
      finally { state.sending = false; button.disabled = false; }
    };
    element.querySelector('.chat-close').onclick = () => { if (!state.sending) closeChat(state); };
    element.querySelector('.chat-minimize').onclick = () => {
      state.minimized = !state.minimized; element.classList.toggle('minimized', state.minimized); rememberWindows();
      if (!state.minimized) sync(state);
    };
    element.querySelector('.chat-history').onclick = () => history(state, true);
    element.querySelector('.chat-latest').onclick = () => history(state);
    windows.set(id, state); windowsRoot.append(element); rememberWindows(); history(state);
    if (window.innerWidth < 768) toggleSidebar(false);
  }
  function closeChat(state) {
    state.closed = true; storage(`draft:${state.id}`, state.element.querySelector('textarea').value);
    state.element.remove(); windows.delete(state.id); rememberWindows();
  }
  function toggleSidebar(open) {
    sidebar.hidden = !open; root.querySelector('.chat-launcher').setAttribute('aria-expanded', String(open));
    if (open) { if (!usersLoaded || Date.now() - lastUsersLoad > 60000) loadUsers(true); if (Date.now() - groupsLoadedAt > 60000) loadGroups(); search.focus(); }
  }
  root.querySelector('.chat-launcher').onclick = () => toggleSidebar(sidebar.hidden);
  root.querySelector('.chat-close-sidebar').onclick = () => toggleSidebar(false);
  root.querySelector('.chat-more-users').onclick = () => { userPage++; loadUsers(); };
  root.querySelector('.chat-more-groups').onclick = () => { groupPage++; loadGroups(false); };
  root.querySelector('.chat-new-group').onclick = () => manageMembers();
  root.querySelector('.chat-support').onclick = async () => {
    const button = root.querySelector('.chat-support'); button.disabled = true;
    try { openGroup(await request(root.dataset.supportUrl, { method: 'POST' })); loadGroups(); }
    catch (error) { statusLabel.textContent = error.message; }
    finally { button.disabled = false; }
  };
  search.oninput = () => { clearTimeout(searchTimer); searchTimer = setTimeout(() => loadUsers(true), 300); };
  const soundButton = root.querySelector('.chat-sound');
  function updateSoundButton() {
    soundButton.innerHTML = `<i class="bi bi-volume-${muted ? 'mute' : 'up'}"></i>`;
    soundButton.title = muted ? 'Activar avisos sonoros' : 'Silenciar avisos'; soundButton.setAttribute('aria-label', soundButton.title);
  }
  soundButton.onclick = () => { muted = !muted; storage('muted', String(muted)); updateSoundButton(); if (!muted) { activity(); sound(); } };
  updateSoundButton();
  const dialog = root.querySelector('.chat-image-dialog'); dialog.querySelector('button').onclick = () => dialog.close();
  dialog.addEventListener('close', () => dialog.querySelector('img').removeAttribute('src'));
  function visibility() {
    if (!document.hidden) { poll(true); windows.forEach(state => { if (!state.minimized) sync(state); }); }
  }
  document.addEventListener('visibilitychange', visibility);
  const timer = setInterval(() => {
    // Mantener la autorización del canal sin contar mensajes en cada latido.
    const reconcile = Date.now() - lastHistoryReconcile > 120000;
    poll(true, Date.now() - lastFullPoll <= 120000);
    if (!document.hidden && !sidebar.hidden && Date.now() - lastUsersLoad > 120000 && !search.value.trim()) loadUsers(true);
    if (!document.hidden && !sidebar.hidden && Date.now() - groupsLoadedAt > 120000) loadGroups();
    // Recupera avisos perdidos o consultas fallidas incluso con Cable conectado.
    if (!document.hidden && reconcile) {
      lastHistoryReconcile = Date.now();
      windows.forEach(state => { if (!state.minimized) sync(state); });
    }
  }, 30000);
  try {
    const restored = JSON.parse(storage('windows') || '[]');
    restored.forEach(user => { openChat(user); if (user.minimized && windows.has(Number(user.id))) { const state = windows.get(Number(user.id)); state.minimized = true; state.element.classList.add('minimized'); } });
  } catch (_) { /* Preferencias antiguas o no disponibles. */ }
  poll();
  stopChat = () => {
    stopped = true; abort.abort(); clearInterval(timer); clearTimeout(searchTimer); clearTimeout(toastTimer); clearTimeout(notificationTimer);
    consumer?.disconnect(); audio?.close(); document.title = originalTitle; if (favicon) favicon.href = originalIcon;
    document.removeEventListener('pointerdown', activity); document.removeEventListener('keydown', activity);
    document.removeEventListener('visibilitychange', visibility);
    windows.forEach(state => storage(`draft:${state.id}`, state.element.querySelector('textarea').value));
    windowsRoot.replaceChildren();
    if (dialog.open) dialog.close();
    const membersDialog = root.querySelector('.chat-members-dialog'); if (membersDialog.open) membersDialog.close();
    dialog.querySelector('img').removeAttribute('src');
    sidebar.hidden = true; root.querySelector('.chat-launcher').setAttribute('aria-expanded', 'false');
    stopChat = null;
  };
}

document.addEventListener('turbolinks:load', initChat);
document.addEventListener('turbolinks:before-cache', () => { if (stopChat) stopChat(); });
